import React from 'react'
import styled, { css } from 'styled-components'
import { Spacings, Text } from '@styles'

const ButtonContainer = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: ${Spacings.SPACING_2B};
	font-family: ${({ theme }) => theme.fontFamily.inter || "'Inter', sans-serif"};
	font-weight: ${Text.BOLD_600};
	border-radius: ${({ theme }) => theme.radius.sm};
	transition: all 0.2s ease-in-out;
	cursor: pointer;
	border: 1px solid transparent;

	/* Size Variations */
	${({ $size }) => {
		if ($size === 'small') {
			return css`
				padding: ${Spacings.SPACING_1B} ${Spacings.SPACING_2B};
				font-size: ${Text.TINY_SMALL};
			`
		}
		if ($size === 'large') {
			return css`
				padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_6B};
				font-size: ${Text.M_LARGE};
			`
		}
		// Base size
		return css`
			padding: ${Spacings.SPACING_5} ${Spacings.SPACING_3B};
			font-size: ${Text.MEDIUM};
		`
	}}

	/* Color & Variant Variations */
	${({ theme, $color, $variant }) => {
		let colorSet
		if ($color === 'primary') colorSet = theme.colors.brand
		else if ($color === 'danger') colorSet = theme.colors.status.error
		else if ($color === 'success') colorSet = theme.colors.status.success
		else if ($color === 'neutral') {
			const neutral = theme?.colors?.neutral || theme?.colors?.status?.neutral || {}
			colorSet = {
				primary: neutral[800] || theme?.colors?.text?.primary,
				hover: neutral[900] || theme?.colors?.text?.primary,
				subtle: neutral[100] || theme?.colors?.surface?.sunken,
				muted: neutral[200] || theme?.colors?.border?.default,
			}
		} else colorSet = theme.colors.brand // default

		if ($variant === 'solid') {
			return css`
				background-color: ${colorSet.primary || colorSet.text};
				color: ${theme.colors.text.inverse};
				&:hover:not(:disabled) {
					background-color: ${colorSet.hover || colorSet.icon};
				}
				&:active:not(:disabled) {
					transform: scale(0.98);
				}
			`
		}

		if ($variant === 'outline') {
			return css`
				background-color: transparent;
				color: ${colorSet.primary || colorSet.text};
				border-color: ${colorSet.primary || colorSet.text};
				&:hover:not(:disabled) {
					background-color: ${colorSet.subtle || colorSet.bg};
				}
				&:active:not(:disabled) {
					transform: scale(0.98);
				}
			`
		}

		if ($variant === 'ghost') {
			return css`
				background-color: transparent;
				color: ${colorSet.primary || colorSet.text};
				&:hover:not(:disabled) {
					background-color: ${colorSet.subtle || colorSet.bg};
				}
				&:active:not(:disabled) {
					transform: scale(0.98);
				}
			`
		}

		if ($variant === 'link') {
			return css`
				background-color: transparent;
				color: ${colorSet.primary || colorSet.text};
				padding: 0 !important;
				height: auto;
				gap: ${Spacings.SPACING_1B};
				font-weight: ${Text.BOLD_500};
				&:hover:not(:disabled) {
					text-decoration: underline;
				}
				svg {
					font-size: ${Spacings.SPACING_14};
				}
			`
		}
	}}

	/* Disabled State */
	&:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	svg {
		font-size: 1.2em;
	}
`

const ButtonV2 = ({
	children,
	variant = 'solid', // 'solid', 'outline', 'ghost'
	color = 'primary', // 'primary', 'danger', 'success', 'neutral'
	size = 'base', // 'small', 'base', 'large'
	iconPrefix,
	iconSuffix,
	loading,
	disabled,
	className,
	...props
}) => {
	return (
		<ButtonContainer $variant={variant} $color={color} $size={size} disabled={disabled || loading} className={className} {...props}>
			{loading ? (
				<span className='spinner'>⌛</span> // Replace with actual spinner if needed
			) : (
				<>
					{iconPrefix && <span style={{ display: 'flex' }}>{iconPrefix}</span>}
					{children}
					{iconSuffix && <span style={{ display: 'flex' }}>{iconSuffix}</span>}
				</>
			)}
		</ButtonContainer>
	)
}

export default ButtonV2
